import { useEffect, useState } from 'react';
import { Icon } from '../../icons';
import CloverLogo from '../../components/ui/CloverLogo';
import { useAuth } from '../../context/AuthContext';
import { sendLoginOtp } from '../../api/auth';
import { toast } from 'react-hot-toast';

const authLightTheme = {
  pageBg: '#f8fafc',
  text: '#0f172a',
  muted: '#64748b',
  subtle: '#94a3b8',
  label: '#475569',
  inputBg: '#ffffff',
  inputBorder: '#e2e8f0',
  inputText: '#0f172a',
  inputFocusBorder: '#4f46e5',
  inputFocusRing: 'rgba(79,70,229,0.1)',
  divider: '#e2e8f0',
  border: '#e2e8f0',
  errorBg: '#fee2e2',
  errorBorder: '#fca5a5',
  errorText: '#991b1b',
  checkboxBg: '#ffffff',
  checkboxBorder: '#d1d5db',
  checkboxText: '#475569',
  ssoBg: '#ffffff',
  ssoBorder: '#e2e8f0',
  ssoText: '#374151',
  ssoHoverBg: '#f5f3ff',
  ssoHoverBorder: '#4f46e5',
};

const authDarkTheme = {
  pageBg: '#020617',
  text: '#f8fafc',
  muted: '#cbd5e1',
  subtle: '#94a3b8',
  label: '#cbd5e1',
  inputBg: '#0f172a',
  inputBorder: '#334155',
  inputText: '#f8fafc',
  inputFocusBorder: '#818cf8',
  inputFocusRing: 'rgba(129,140,248,0.14)',
  divider: '#334155',
  border: '#334155',
  errorBg: 'rgba(127,29,29,0.34)',
  errorBorder: '#7f1d1d',
  errorText: '#fca5a5',
  checkboxBg: '#0f172a',
  checkboxBorder: '#475569',
  checkboxText: '#e2e8f0',
  ssoBg: '#111827',
  ssoBorder: '#334155',
  ssoText: '#e2e8f0',
  ssoHoverBg: '#1e293b',
  ssoHoverBorder: '#818cf8',
};

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const root = document.documentElement;
    const syncTheme = () => setIsDark(root.classList.contains('dark'));

    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

const getAuthTheme = (isDark) => (isDark ? authDarkTheme : authLightTheme);

function AuthField({ label, type = "text", value, onChange, placeholder, note, icon, showToggle }) {
  const isDark = useDarkMode();
  const theme = getAuthTheme(isDark);
  const [show, setShow] = useState(false);
  const actualType = showToggle ? (show ? "text" : "password") : type;
  return (
    <div>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.label, marginBottom: 6 }}>{label}</label>}
      <div style={{ position: "relative" }}>
        {icon && <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: theme.subtle, display: "flex" }}><Icon name={icon} size={15} color={theme.subtle} /></div>}
        <input type={actualType} value={value} onChange={onChange} placeholder={placeholder}
          style={{ width: "100%", padding: icon ? "12px 40px 12px 38px" : "12px 40px 12px 14px", border: `1.5px solid ${theme.inputBorder}`, borderRadius: 10, fontSize: 14, color: theme.inputText, background: theme.inputBg, outline: "none", transition: "border-color .15s, box-shadow .15s", boxSizing: "border-box", fontFamily: "inherit" }}
          onFocus={e => { e.target.style.borderColor = theme.inputFocusBorder; e.target.style.boxShadow = `0 0 0 3px ${theme.inputFocusRing}`; }}
          onBlur={e => { e.target.style.borderColor = theme.inputBorder; e.target.style.boxShadow = "none"; }}
        />
        {showToggle && <button type="button" onClick={() => setShow(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: theme.subtle, display: "flex", padding: 0, minWidth: 44, minHeight: 44, alignItems: "center", justifyContent: "center" }}><Icon name="eye" size={15} color={theme.subtle} /></button>}
      </div>
      {note && <p style={{ fontSize: 11, color: theme.subtle, marginTop: 4 }}>{note}</p>}
    </div>
  );
}

function AuthPanel() {
  const features = [
    { icon: "maintenance", label: "Maintenance Billing", desc: "Track dues, record payments, generate receipts" },
    { icon: "vendors", label: "Vendor & AMC Mgmt", desc: "Manage contracts with expiry alerts" },
    { icon: "bank", label: "Bank Accounts", desc: "Secure multi-account financial overview" },
    { icon: "audit", label: "Audit Log", desc: "Complete trail of every action taken" },
  ];
  const stats = [{ value: "2,400+", label: "Societies" }, { value: "₹18Cr+", label: "Processed" }, { value: "99.9%", label: "Uptime" }];
  return (
    <div className="hidden lg:flex" style={{ width: 420, flexShrink: 0, background: "linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)", position: "relative", overflow: "hidden", flexDirection: "column", padding: "48px 40px" }}>
      <div style={{ position: "absolute", top: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -60, right: -60, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "40%", right: 20, width: 140, height: 140, borderRadius: "50%", background: "radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", marginBottom: 48, position: "relative" }}>
        <CloverLogo width={180} height={49} />
      </div>
      <div style={{ position: "relative", marginBottom: 40 }}>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.02em", marginBottom: 12 }}>
          Everything your society needs,{" "}
          <span style={{ background: "linear-gradient(135deg,#818cf8,#06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>in one place.</span>
        </div>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7 }}>From maintenance billing to vendor AMCs - manage your housing society with confidence and clarity.</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 40, position: "relative" }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, backdropFilter: "blur(4px)" }}>
              <Icon name={f.icon} size={15} color="rgba(255,255,255,0.65)" strokeWidth={1.6} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)", marginBottom: 2 }}>{f.label}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: "relative", display: "flex", gap: 0, marginTop: "auto", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, overflow: "hidden" }}>
        {stats.map((s, i) => (
          <div key={i} style={{ flex: 1, padding: "14px 10px", textAlign: "center", borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 2, textTransform: "uppercase", letterSpacing: ".06em" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LoginScreen({ onLogin, onSignup, onForgot, initialEmail }) {
  const { login, loginWithOtp, pendingTwoFactor, verifyTwoFactor, clearPendingTwoFactor } = useAuth();
  const isDark = useDarkMode();
  const theme = getAuthTheme(isDark);
  const [loginMethod, setLoginMethod] = useState("password"); // 'password' or 'otp'

  const getInitialEmail = () => {
    if (initialEmail) return initialEmail;
    try {
      const params = new URLSearchParams(window.location.search);
      const urlEmail = params.get('email');
      if (urlEmail) return urlEmail;
    } catch (_) {}
    try {
      const stored = localStorage.getItem('registered_email');
      if (stored) return stored;
    } catch (_) {}
    return "";
  };

  const [email, setEmail] = useState(getInitialEmail);
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [loginOtpCode, setLoginOtpCode] = useState("");
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const urlEmail = params.get('email');
      if (urlEmail) {
        setEmail(urlEmail);
        return;
      }
      const stored = localStorage.getItem('registered_email');
      if (stored) {
        setEmail(stored);
      }
    } catch (_) {}
  }, [initialEmail]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (!pendingTwoFactor) {
      setTwoFactorCode("");
    }
  }, [pendingTwoFactor]);

  const handleSendLoginOtp = async () => {
    if (!email) { setError("Please enter your email or phone number."); return; }
    setError("");
    setLoading(true);
    try {
      const channel = email.includes('@') ? 'mail' : 'sms';
      await sendLoginOtp(email, channel);
      setLoginOtpSent(true);
      setResendTimer(60);
      toast.success("OTP sent successfully");
    } catch (err) {
      const message = err.errors ? Object.values(err.errors).flat()[0] : err.message;
      setError(message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (pendingTwoFactor) {
      if (!twoFactorCode) {
        setError("Please enter the verification code.");
        return;
      }
      setError("");
      setLoading(true);
      try {
        await verifyTwoFactor(twoFactorCode);
        try { localStorage.removeItem('registered_email'); } catch (_) {}
        onLogin();
      } catch (err) {
        const message = err.errors ? Object.values(err.errors).flat()[0] : err.message;
        if (message?.toLowerCase().includes('2fa login session has expired')) {
          clearPendingTwoFactor();
          setTwoFactorCode("");
        }
        setError(message || "Invalid verification code.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (loginMethod === 'otp') {
      if (!email || !loginOtpCode) { setError("Please enter your email/phone and OTP."); return; }
      setError("");
      setLoading(true);
      try {
        const response = await loginWithOtp({ contact: email, code: loginOtpCode });
        try { localStorage.removeItem('registered_email'); } catch (_) {}
        onLogin();
      } catch (err) {
        const message = err.errors ? Object.values(err.errors).flat()[0] : err.message;
        setError(message || "Invalid OTP.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!email || !password) { setError("Please enter your email and password."); return; }
    setError("");
    setLoading(true);

    try {
      const response = await login({ email, password });
      if (!response?.requires_2fa) {
        try { localStorage.removeItem('registered_email'); } catch (_) {}
        onLogin();
      }
    } catch (err) {
      const message = err.errors ? Object.values(err.errors).flat()[0] : err.message;
      setError(message || "Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: theme.pageBg }}>
      <AuthPanel />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
        <div style={{ width: "100%", maxWidth: 420, animation: "fadeUp .4s ease forwards" }}>

          {/* Mobile logo - only visible when sidebar panel is hidden */}
          <div className="flex lg:hidden items-center justify-center mb-8">
            <CloverLogo width={160} height={44} />
          </div>

          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: theme.text, letterSpacing: "-0.02em", marginBottom: 6 }}>Welcome back!</div>
            <div style={{ fontSize: 13, color: theme.muted }}>Sign in to your The Clover App account</div>
          </div>
          {error && (
            <div style={{ background: theme.errorBg, border: `1px solid ${theme.errorBorder}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: theme.errorText, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="alert" size={13} color={theme.errorText} /> {error}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <AuthField label="Email or Phone Number" type="text" icon="mail" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com or 9876543210" />
            
            {!pendingTwoFactor && loginMethod === 'password' && (
              <div>
                <AuthField label="Password" icon="lock" showToggle value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                  <button onClick={onForgot} style={{ fontSize: 12, color: isDark ? '#818cf8' : '#4f46e5', fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: "4px 0", fontFamily: "inherit" }}>Forgot password?</button>
                </div>
              </div>
            )}

            {!pendingTwoFactor && loginMethod === 'otp' && (
              <div>
                {loginOtpSent ? (
                  <div>
                    <AuthField label="OTP Code" icon="lock" value={loginOtpCode} onChange={e => setLoginOtpCode(e.target.value)} placeholder="Enter 6-digit code" />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                      <button onClick={() => setLoginOtpSent(false)} style={{ fontSize: 12, color: isDark ? '#cbd5e1' : '#64748b', fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: "4px 0", fontFamily: "inherit" }}>Change Email/Phone</button>
                      <button onClick={handleSendLoginOtp} disabled={loading || resendTimer > 0} style={{ fontSize: 12, color: (resendTimer > 0) ? theme.subtle : (isDark ? '#818cf8' : '#4f46e5'), fontWeight: 600, background: "none", border: "none", cursor: (loading || resendTimer > 0) ? "default" : "pointer", padding: "4px 0", fontFamily: "inherit" }}>
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : (loading ? "Sending..." : "Resend OTP")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={handleSendLoginOtp} disabled={loading} style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1px solid ${theme.inputBorder}`, cursor: loading ? "not-allowed" : "pointer", background: theme.inputBg, color: theme.text, fontSize: 14, fontWeight: 600, fontFamily: "inherit", transition: "all .2s" }}>
                    {loading ? "Sending..." : "Send OTP"}
                  </button>
                )}
              </div>
            )}

            {pendingTwoFactor && (
              <div>
                <AuthField label="Verification Code" icon="lock" value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value)} placeholder="Enter 6-digit code" />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <button onClick={() => { clearPendingTwoFactor(); setTwoFactorCode(""); setError(""); }} style={{ fontSize: 12, color: isDark ? '#cbd5e1' : '#64748b', fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: "4px 0", fontFamily: "inherit" }}>Back</button>
                  <span style={{ fontSize: 12, color: theme.subtle }}>Use your authenticator app or recovery code</span>
                </div>
              </div>
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
              <div onClick={() => setRemember(r => !r)} style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${remember ? (isDark ? '#818cf8' : '#4f46e5') : theme.checkboxBorder}`, background: remember ? (isDark ? '#818cf8' : '#4f46e5') : theme.checkboxBg, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s", flexShrink: 0 }}>
                {remember && <Icon name="check" size={10} color="white" strokeWidth={3} />}
              </div>
              <span style={{ fontSize: 13, color: theme.checkboxText, fontWeight: 500 }}>Keep me signed in</span>
            </label>
            {(!loginMethod || (loginMethod === 'otp' && !loginOtpSent)) ? null : (
              <button onClick={handleSubmit} disabled={loading}
                style={{ width: "100%", padding: "14px", borderRadius: 11, border: "none", cursor: loading ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 15, fontWeight: 700, boxShadow: "0 4px 16px rgba(79,70,229,0.35)", transition: "all .2s", fontFamily: "inherit", opacity: loading ? 0.75 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; }}>
                {loading ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> {pendingTwoFactor ? "Verifying…" : "Signing in…"}</> : (pendingTwoFactor ? "Verify Code" : "Sign In to Dashboard")}
              </button>
            )}

            {!pendingTwoFactor && (
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0" }}>
                <div style={{ flex: 1, height: 1, background: theme.divider }} />
                <span style={{ fontSize: 11, color: theme.subtle, fontWeight: 500 }}>or continue with</span>
                <div style={{ flex: 1, height: 1, background: theme.divider }} />
              </div>
            )}
            
            {!pendingTwoFactor && (
              <button onClick={() => {
                setLoginMethod(m => m === 'password' ? 'otp' : 'password');
                setLoginOtpSent(false);
                setError("");
              }} style={{ width: "100%", padding: "12px", borderRadius: 10, border: `1.5px solid ${theme.inputBorder}`, cursor: "pointer", background: "transparent", color: theme.text, fontSize: 14, fontWeight: 600, fontFamily: "inherit", transition: "all .2s" }}>
                {loginMethod === 'password' ? "Sign in with OTP" : "Sign in with Password"}
              </button>
            )}
          </div>
          {/* <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
            <div style={{ flex: 1, height: 1, background: theme.divider }} />
            <span style={{ fontSize: 11, color: theme.subtle, fontWeight: 500 }}>or continue with</span>
            <div style={{ flex: 1, height: 1, background: theme.divider }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[{ label: "Google SSO", icon: "🔵" }, { label: "Microsoft", icon: "🟦" }].map((p, i) => (
              <button key={i} style={{ padding: "11px 14px", border: `1.5px solid ${theme.ssoBorder}`, borderRadius: 10, background: theme.ssoBg, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, fontWeight: 600, color: theme.ssoText, transition: "all .15s", fontFamily: "inherit" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.ssoHoverBorder; e.currentTarget.style.background = theme.ssoHoverBg; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.ssoBorder; e.currentTarget.style.background = theme.ssoBg; }}>
                <span style={{ fontSize: 16 }}>{p.icon}</span><span>{p.label}</span>
              </button>
            ))}
          </div> */}
          <p style={{ textAlign: "center", fontSize: 13, color: theme.muted }} className='mt-4'>
            Don't have an account?{" "}
            <button onClick={onSignup} style={{ color: "#4f46e5", fontWeight: 700, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Register your society →</button>
          </p>
          <p style={{ textAlign: "center", fontSize: 11, color: theme.subtle, marginTop: 16, lineHeight: 1.6 }}>
            By signing in you agree to our <span style={{ color: isDark ? '#818cf8' : '#4f46e5', cursor: "pointer" }}>Terms of Service</span> and <span style={{ color: isDark ? '#818cf8' : '#4f46e5', cursor: "pointer" }}>Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}
